import React from 'react'
import { ESellerPermission } from 'models/sellerEmployees'
import { adminRoutes } from 'shared/constants/routes'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { appSelectors } from 'store/app'

const OWNER_ONLY = 'OWNER_ONLY'
type TSectionRule = ESellerPermission | typeof OWNER_ONLY

const ROOT_SECTIONS: Record<string, ESellerPermission> = {
  goods: ESellerPermission.GOODS,
  horses: ESellerPermission.GOODS,
  'ai-goods': ESellerPermission.GOODS,
  orders: ESellerPermission.ORDERS,
  promocodes: ESellerPermission.PROMOCODES,
  discounts: ESellerPermission.DISCOUNTS
}

const PROFILE_SECTIONS: Record<string, TSectionRule> = {
  [adminRoutes.PROFILE_CHATS]: ESellerPermission.CHATS,
  [adminRoutes.PROFILE_SHOPS]: ESellerPermission.SHOPS,
  [adminRoutes.PROFILE_DELIVERY]: ESellerPermission.DELIVERY,
  [adminRoutes.PROFILE_ACCOUNTING]: ESellerPermission.ACCOUNTING,
  [adminRoutes.PROFILE_MASS_IMPORT]: ESellerPermission.MASS_IMPORT,
  [adminRoutes.PROFILE_API_ONE_C]: ESellerPermission.API_SYNC,
  [adminRoutes.PROFILE_API_MOYSKLAD]: ESellerPermission.API_SYNC,
  [adminRoutes.PROFILE_API_INSALES]: ESellerPermission.API_SYNC,
  [adminRoutes.PROFILE_PROMOCODES]: ESellerPermission.PROMOCODES,
  [adminRoutes.PROFILE_DISCOUNTS]: ESellerPermission.DISCOUNTS,
  [adminRoutes.PROFILE_REVIEWS]: ESellerPermission.REVIEWS,
  [adminRoutes.PROFILE_EMPLOYEES]: OWNER_ONLY
}

// Без раздела «Сообщения» страница чатов остаётся открытой: в ней «Поддержка»,
// а бэкенд отдаёт такому сотруднику только его обращения
const CHATS_PATH = `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_CHATS}`

const getSectionRule = (pathname: string): TSectionRule | null => {
  const [, root = '', section = ''] = pathname.split('/')
  if (`/${root}` === adminRoutes.PROFILE)
    return PROFILE_SECTIONS[section] ?? null
  return ROOT_SECTIONS[root] ?? null
}

/**
 * Что из кабинета продавца открыто текущему аккаунту. Владельцу — всё, сотруднику —
 * разделы из его прав; пока права не загружены, isReady = false
 */
export const useSellerAccess = () => {
  const employeeId = useAppSelector(appSelectors.selectEmployeeId)
  const employee = useAppSelector(appSelectors.selectEmployee)
  const isEmployee = Boolean(employeeId)
  const permissions = employee?.permissions

  const can = React.useCallback(
    (permission: ESellerPermission) =>
      !isEmployee || Boolean(permissions?.includes(permission)),
    [isEmployee, permissions]
  )

  // Для пунктов меню: «Сообщения» без права скрываем, хотя страница открыта
  const canSeeSection = React.useCallback(
    (pathname: string) => {
      const rule = getSectionRule(pathname)
      if (!rule || !isEmployee) return true
      return rule !== OWNER_ONLY && can(rule)
    },
    [isEmployee, can]
  )

  const canOpenPath = React.useCallback(
    (pathname: string) =>
      pathname.startsWith(CHATS_PATH) || canSeeSection(pathname),
    [canSeeSection]
  )

  const homePath = React.useMemo(() => {
    if (can(ESellerPermission.ORDERS)) return adminRoutes.ORDERS
    if (can(ESellerPermission.GOODS)) return adminRoutes.GOODS
    return `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_INFO}`
  }, [can])

  return {
    isEmployee,
    employee,
    isReady: !isEmployee || Boolean(employee),
    can,
    canSeeSection,
    canOpenPath,
    homePath
  }
}
